import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { uid } from '../../shared/utils/helpers';
import { assignCourts, updateConsecutiveGames } from '../../shared/logic/matchmaking';
import { ratingLabel } from '../../shared/logic/scoring';
import RotatePlayLogo from '../components/RotatePlayLogo';
import { KioskRestService } from '../services/kioskRestService';
import type { League, Player, Session as SessionType, GameRecord, Court } from '../../shared/types';

const API_URL = import.meta.env.VITE_API_URL as string;

export default function KioskSession() {
  const { leagueId }   = useParams<{ leagueId: string }>();
  const [searchParams] = useSearchParams();
  const token          = searchParams.get('token') ?? '';

  const [league,    setLeague]    = useState<League | null>(null);
  const [players,   setPlayers]   = useState<Player[]>([]);
  const [session,   setSession]   = useState<SessionType | null>(null);
  const [games,     setGames]     = useState<GameRecord[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [authError, setAuthError] = useState(false);
  const [error,     setError]     = useState('');

  const svcRef       = useRef<KioskRestService | null>(null);
  const sessionIdRef = useRef(uid());

  const [checkedIn,     setCheckedIn]     = useState<Set<string>>(new Set());
  const [sessionCourts, setSessionCourts] = useState(4);
  const [scoreEdits,    setScoreEdits]    = useState<Record<string, string>>({});

  useEffect(() => {
    if (!leagueId || !token) {
      setAuthError(true);
      setLoading(false);
      return;
    }

    const svc = new KioskRestService({
      apiUrl:        API_URL,
      leagueId,
      delegateToken: token,
      onAuthError:   () => { setAuthError(true); setLoading(false); },
      onFirstLoad:   () => setLoading(false),
    });
    svcRef.current = svc;

    const unsubs = [
      svc.onLeague(setLeague),
      svc.onPlayers(setPlayers),
      svc.onSession(s => {
        setSession(s);
        if (s?.checkedIn) {
          setCheckedIn(new Set(s.checkedIn));
          setSessionCourts(s.maxCourts);
        }
      }),
      svc.onGames(setGames),
    ];

    return () => {
      unsubs.forEach(fn => fn());
      svc.destroy();
      svcRef.current = null;
    };
  }, [leagueId, token]);

  useEffect(() => {
    setScoreEdits({});
  }, [session?.round]);

  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id;

  function toggleCheckin(id: string) {
    const next = new Set(checkedIn);
    if (next.has(id)) next.delete(id); else next.add(id);
    setCheckedIn(next);
    setSessionCourts(Math.min(Math.floor(next.size / 4), league?.courts ?? 4));
  }

  async function startSession() {
    if (checkedIn.size < 4) { alert('Need at least 4 players checked in.'); return; }
    setError('');
    const ids = [...checkedIn];
    const { courts, waiting } = assignCourts({
      checkedInIds:     ids,
      consecutiveGames: {},
      maxCourts:        sessionCourts,
      games,
      players,
    });
    const newSession: SessionType = {
      courts, waiting,
      checkedIn:        ids,
      benched:          [],
      round:            1,
      consecutiveGames: {},
      maxCourts:        sessionCourts,
    };
    try {
      await svcRef.current!.setSession(newSession);
    } catch {
      setError('Failed to start session. Check your connection.');
    }
  }

  async function closeRound(andEnd: boolean) {
    if (!session) return;
    setError('');

    const updatedCourts: Court[] = session.courts.map((c, i) => {
      if (c.submitted) return c;
      const sA = scoreEdits[`A-${i}`] ?? '';
      const sB = scoreEdits[`B-${i}`] ?? '';
      return {
        ...c,
        scoreA: sA !== '' ? parseInt(sA) : null,
        scoreB: sB !== '' ? parseInt(sB) : null,
      };
    });

    const hasUnscored = updatedCourts.some(c => c.scoreA == null || c.scoreB == null);
    if (hasUnscored && !confirm('Some courts are missing scores. Close the round anyway?')) return;

    const game: GameRecord = {
      id:      uid(),
      date:    new Date().toISOString(),
      session: sessionIdRef.current,
      round:   session.round,
      courts:  updatedCourts,
    };

    try {
      await svcRef.current!.addGame(game);
    } catch {
      setError('Failed to save game record. Check your connection.');
      return;
    }

    if (andEnd) {
      await svcRef.current!.setSession(null);
    } else {
      const newConsec = updateConsecutiveGames({ ...session, courts: updatedCourts });
      const { courts, waiting } = assignCourts({
        checkedInIds:     session.checkedIn,
        consecutiveGames: newConsec,
        maxCourts:        session.maxCourts,
        games:            [...games, game],
        players,
      });
      await svcRef.current!.setSession({
        ...session,
        courts, waiting,
        round:            session.round + 1,
        consecutiveGames: newConsec,
      });
    }
  }

  async function reshuffleCourts() {
    if (!session) return;
    if (!confirm('Reshuffle? All submitted scores this round will be lost.')) return;
    const { courts, waiting } = assignCourts({
      checkedInIds:     session.checkedIn,
      consecutiveGames: session.consecutiveGames,
      maxCourts:        session.maxCourts,
      games,
      players,
    });
    await svcRef.current!.setSession({ ...session, courts, waiting });
    setScoreEdits({});
  }

  if (authError) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,.2)', padding: '36px 30px', maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <RotatePlayLogo size={60} animated />
          <div style={{ fontSize: '2.5rem', marginBottom: 10, marginTop: 16 }}>🔒</div>
          <h2 style={{ fontWeight: 800, color: 'var(--red)', marginBottom: 8 }}>Invalid Session Link</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: '.875rem' }}>
            This link has expired or is invalid. Ask your league organizer for a fresh delegate URL.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--gray-400)' }}>Connecting…</p>
      </div>
    );
  }

  const leagueMax      = league?.courts ?? 4;
  const isSmartMatch   = (session?.maxCourts ?? 0) >= 3 && games.length >= 8;
  const submittedCount = session?.courts.filter(c => c.submitted).length ?? 0;
  const totalCourts    = session?.courts.length ?? 0;
  const waitingList    = session?.waiting ?? [];

  const Header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <RotatePlayLogo size={28} animated />
      <div>
        <div style={{ fontWeight: 800, fontSize: '1rem' }}>{league?.name ?? 'RotatePlay'}</div>
        <div style={{ fontSize: '.75rem', color: 'var(--gray-500)' }}>Delegate Session</div>
      </div>
    </div>
  );

  // ── Check-in phase ────────────────────────────────────────

  if (!session) {
    const maxUsable = Math.min(Math.floor(checkedIn.size / 4), leagueMax);
    const waiting   = checkedIn.size - maxUsable * 4;

    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        <Header />
        {error && <div className="alert alert-red" style={{ marginBottom: 14 }}>{error}</div>}

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Check-In <span style={{ fontWeight: 400, fontSize: '.85rem', color: 'var(--gray-500)' }}>({checkedIn.size} selected)</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const next = new Set(players.map(p => p.id));
                setCheckedIn(next);
                setSessionCourts(Math.min(Math.floor(next.size / 4), leagueMax));
              }}>All</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setCheckedIn(new Set()); setSessionCourts(0); }}>None</button>
            </div>
          </div>

          {!players.length ? (
            <div className="empty">
              <div className="empty-icon">👤</div>
              No players registered yet.
            </div>
          ) : (
            <div className="player-grid">
              {players.map(p => {
                const checked = checkedIn.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`player-card ${checked ? 'checked-in' : ''}`}
                    onClick={() => toggleCheckin(p.id)}
                  >
                    <div className="pname">{p.name} {checked ? '✅' : ''}</div>
                    <div className="meta">
                      <span className="badge badge-blue">{ratingLabel(p)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {checkedIn.size >= 4 && (
          <div className="card">
            <div className="card-title">Courts for This Session</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="number" min={1} max={leagueMax}
                value={sessionCourts}
                onChange={e => setSessionCourts(Math.min(parseInt(e.target.value) || 1, leagueMax))}
                style={{ width: 72 }}
              />
              <span style={{ fontSize: '.85rem', color: 'var(--gray-500)' }}>
                (League default: {leagueMax})
              </span>
            </div>
            {waiting > 0 && (
              <div className="alert alert-info" style={{ marginTop: 10 }}>
                With {maxUsable} court{maxUsable !== 1 ? 's' : ''}, <strong>{waiting} player{waiting !== 1 ? 's' : ''}</strong> will be on the waiting list this round.
              </div>
            )}
            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 14 }}
              onClick={startSession}
            >
              Start Session ({checkedIn.size} players, {maxUsable} courts)
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Active session phase ──────────────────────────────────

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <Header />
      {error && <div className="alert alert-red" style={{ marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.3rem' }}>Round {session.round}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={reshuffleCourts}>Reshuffle</button>
          <button className="btn btn-yellow btn-sm" onClick={() => closeRound(false)}>Next Round</button>
          <button className="btn btn-danger btn-sm" onClick={() => closeRound(true)}>End Session</button>
        </div>
      </div>

      <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        {submittedCount === totalCourts ? (
          <span style={{ color: 'var(--green-d)', fontWeight: 700 }}>
            All {totalCourts} courts submitted — ready to close the round.
          </span>
        ) : (
          <span>
            <strong>{submittedCount} of {totalCourts}</strong> courts submitted via kiosk. Enter missing scores below.
          </span>
        )}
      </div>

      {session.courts.map((c, i) => (
        <div
          key={i}
          className="court-card"
          style={{ borderColor: c.submitted ? 'var(--green)' : undefined }}
        >
          <div className="court-header">
            <div className="court-title">Court {i + 1}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {c.submitted
                ? <span className="badge badge-green">Submitted</span>
                : <span className="badge badge-gray">Pending</span>
              }
              <span className={`badge ${isSmartMatch ? 'badge-blue' : 'badge-gray'}`}>
                {isSmartMatch ? 'Smart Match' : 'Random'}
              </span>
            </div>
          </div>

          <div className="teams">
            <div className="team">
              <div className="team-label">Team A</div>
              {c.teamA.map(id => <div key={id} className="team-player">{playerName(id)}</div>)}
            </div>
            <div className="vs">VS</div>
            <div className="team">
              <div className="team-label">Team B</div>
              {c.teamB.map(id => <div key={id} className="team-player">{playerName(id)}</div>)}
            </div>
          </div>

          {!c.submitted && (
            <div className="score-row" style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--gray-500)' }}>Score:</span>
              <span style={{ fontSize: '.82rem' }}>Team A</span>
              <input
                className="score-input"
                type="number" min={0} max={99}
                value={scoreEdits[`A-${i}`] ?? (c.scoreA != null ? c.scoreA : '')}
                placeholder="0"
                onChange={e => setScoreEdits(prev => ({ ...prev, [`A-${i}`]: e.target.value }))}
              />
              <span style={{ fontWeight: 700, color: 'var(--gray-500)' }}>–</span>
              <input
                className="score-input"
                type="number" min={0} max={99}
                value={scoreEdits[`B-${i}`] ?? (c.scoreB != null ? c.scoreB : '')}
                placeholder="0"
                onChange={e => setScoreEdits(prev => ({ ...prev, [`B-${i}`]: e.target.value }))}
              />
              <span style={{ fontSize: '.82rem' }}>Team B</span>
            </div>
          )}

          {c.submitted && (
            <div style={{ marginTop: 10, textAlign: 'center', fontWeight: 800, fontSize: '1.2rem', color: 'var(--green-d)' }}>
              {c.scoreA} – {c.scoreB}
            </div>
          )}
        </div>
      ))}

      {waitingList.length > 0 && (
        <div className="card">
          <div className="card-title">Sitting Out This Round</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {waitingList.map(id => {
              const streak = session.consecutiveGames[id] ?? 0;
              return (
                <div key={id} style={{
                  background: '#fff', border: '1.5px solid var(--gray-300)',
                  borderRadius: 99, padding: '4px 12px', fontSize: '.82rem', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {playerName(id)}
                  {streak >= 3 && <span className="badge badge-red">3-game rest</span>}
                  {streak > 0 && streak < 3 && <span className="badge badge-gray">{streak}-game streak</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
